/**
 * Downloads domain — non-hook helpers.
 *
 * The async export endpoints are invoked imperatively inside the download
 * widget's `initiate()` callback, so this domain has no cache-invalidating
 * mutations (and therefore no `invalidation.ts`). The only helper is a thin
 * wrapper that adapts the generated path-param status client fn to the
 * `(requestId: number)` signature the download widget expects.
 */

import { getDownloadStatus as generatedGetDownloadStatus } from '../generated/clients/getDownloadStatus'

/**
 * Status shape polled by the download widget. Mirrors the generated
 * `GetDownloadStatusQueryResponse`, but diverges in two deliberate spots:
 * `status` is narrowed to the precise union the widget switches on (the
 * generated field is a plain string), and `download_url` is declared
 * `string` even though the generated/runtime value can be `null` — the
 * widget's truthy guard handles the `null` case.
 */
export interface DownloadStatusResponse {
  request_id: number
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'expired'
  progress_percentage: number
  item_count: number
  download_url?: string
  detail?: string
  expires_at?: string
}

/**
 * Poll the status of an async download request.
 *
 * Wraps the generated `getDownloadStatus` client fn (which takes a
 * `{ request_id }` path-param object) so callers and the widget can keep
 * passing a bare numeric request id.
 */
export function getDownloadStatus(
  requestId: number
): Promise<DownloadStatusResponse> {
  return generatedGetDownloadStatus({
    request_id: requestId,
  }) as Promise<DownloadStatusResponse>
}
